import { GameScene, pickPreviewSlotForFx } from '../GameScene';
import { ParticleManager } from '../../systems/ParticleManager';
import { PARTICLE_SHEET } from '../../systems/ParticleSystem';
import type { EntityAnchorSlot, EntityRenderable, ItemGroundRenderable } from '../../systems/AnchorResolver';
import { getParticleEffect } from '@shared/data/particleEffects';
import { ParticlePanel } from '../ui/ParticlePanel';
import type { ParticleEffectDefV2 } from '@shared/types/particles';
import type { MapData } from '@shared/types/map';
import { Direction } from '@shared/types/entity';
import { ClientMessageType, type PlayEntityFxMessage } from '@shared/types/messages';
import { TILE_SIZE } from '@shared/constants';

/**
 * Partículas / FX cosméticos: panel dev de FX (Ctrl+P), manager lazy de partículas, disparo de FX
 * sobre el player local (test), FX de entidad enviados por el server (handlePlayEntityFx), swing de
 * melee, partículas de mapa (registerMapParticles) y el tick de emitters (updateActiveParticleEmitters,
 * update loop). Extraído de GameScene (god-object). El estado (particlePanel/particleManager/
 * mapParticleHandles/activeNativeHandles) vive en GameScene (host, lo tocan otros FX) y se accede vía
 * `this.scene.*`. Ver docs/PARTICLES.md.
 */
export class ParticleFxController {
  constructor(private scene: GameScene) {}

  public createParticlePanel(): void {
    const { width, height } = this.scene.scale;
    const savedOptions = this.scene.persistence.loadClientOptions();
    const panelX = savedOptions?.particlePanelX ?? width / 2;
    const panelY = savedOptions?.particlePanelY ?? height / 2;

    // Asegurar que el spritesheet de partículas está cargado (lo necesita el motor v1).
    if (!this.scene.textures.exists(PARTICLE_SHEET.key)) {
      this.scene.load.spritesheet(
        PARTICLE_SHEET.key,
        `assets/${PARTICLE_SHEET.path}`,
        { frameWidth: PARTICLE_SHEET.frameWidth, frameHeight: PARTICLE_SHEET.frameHeight }
      );
      this.scene.load.start();
    }

    this.scene.particlePanel = new ParticlePanel(
      this.scene,
      {
        onMove: (x, y) => this.scene.persistence.savePanelPosition('particle', x, y),
        onPlayFx: (fxId, def) => this.playParticleFxOnLocalPlayer(fxId, def),
        onShootFx: (fxId, def, wx, wy) => this.shootParticleFxFromLocalPlayer(fxId, def, wx, wy),
        onBroadcastAura: (fxId) => this.scene.entityVfx.broadcastAuraToggle(fxId),
        onClearAll: () => this.clearAllParticleFxFromLocalPlayer(),
      },
      { initialX: panelX, initialY: panelY }
    );
  }

  public clearAllParticleFxFromLocalPlayer(): void {
    if (!this.scene.localSessionId) return;

    // 1) Stop de handles propios (invocados/shooteados manualmente)
    const mgr = this.scene.particleManager;
    if (mgr) {
      for (const h of this.scene.activeNativeHandles) mgr.stop(h);
    }
    this.scene.activeNativeHandles = [];

    // 2) Stop de los emitters equipped del local player (FX bound a item
    //    visualIds). El próximo sync de PlayerState los va a respawnear
    //    porque el item sigue equipado — el clear es temporal, útil para
    //    previewear otros FX sobre el arma sin interferencia.
    if (mgr) this.scene.entityVfx.clearEquippedVfxFor(this.scene.localSessionId);

    // 3) Toggle-off de auras sincronizadas via server
    const playerState = this.scene.room?.state.players.get(this.scene.localSessionId);
    const auraFxIds = (playerState as unknown as { auraFxIds?: { length: number; [k: number]: string } } | undefined)?.auraFxIds;
    if (auraFxIds && this.scene.colyseusClient) {
      const ids: string[] = [];
      for (let i = 0; i < auraFxIds.length; i++) ids.push(auraFxIds[i]);
      for (const fxId of ids) {
        this.scene.colyseusClient.send({
          type: ClientMessageType.TOGGLE_AURA_FX,
          fxId,
        } as never);
      }
    }
    console.log('[ParticleFX] 🧹 Clear → handles locales + equipped + toggle-off auras');
  }

  public applyCameraFxFromDef(def: ParticleEffectDefV2): void {
    if (def.screenShake) {
      this.scene.cameras.main.shake(def.screenShake.duration, def.screenShake.intensity);
    }
    if (def.screenFlash) {
      const c = def.screenFlash.color ?? 0xffffff;
      const r = (c >> 16) & 0xff;
      const g = (c >> 8) & 0xff;
      const b = c & 0xff;
      this.scene.cameras.main.flash(def.screenFlash.duration, r, g, b);
    }
  }

  public shootParticleFxFromLocalPlayer(
    fxId: string,
    def: ParticleEffectDefV2,
    worldX: number,
    worldY: number
  ): string {
    if (!this.scene.localSessionId) return 'v1';
    const obj = this.scene.playerObjects.get(this.scene.localSessionId);
    if (!obj) return 'v1';

    this.applyCameraFxFromDef(def);

    const mgr = this.ensureParticleManager();
    const dx = worldX - obj.sprite.x;
    const dy = worldY - (obj.sprite.y - obj.sprite.displayHeight / 2);
    const distance = Math.sqrt(dx * dx + dy * dy);
    // 200 px/s velocidad base de projectile. A 400 el viaje se siente como un
    // flash imperceptible — 200 lo hace claramente visible sin que se sienta lento.
    const durationMs = Math.max(300, (distance / 200) * 1000);

    const handle = mgr.spawn(fxId, {
      kind: 'projectile',
      from: { kind: 'entity', entityId: this.scene.localSessionId, slot: 'center' },
      to: { kind: 'world', x: worldX, y: worldY },
      durationMs,
      startedAt: this.scene.time.now,
    });
    if (!handle) {
      console.warn(`[ParticleFX] SHOOT spawn falló para ${fxId}`);
      return 'v1';
    }
    this.scene.activeNativeHandles.push(handle);
    const motor = (def.orbit || def.depthMode === 'orbit' || def.subEmitters || def.trail || def.noise)
      ? 'v1'
      : 'v2';
    console.log(`[ParticleFX] ${fxId} → SHOOT ${motor} (${distance.toFixed(0)}px @ ${durationMs.toFixed(0)}ms)`);
    return motor;
  }

  public openParticlePanel(): void {
    if (!this.scene.particlePanel) this.createParticlePanel();
    this.scene.particlePanel?.open();
  }

  public toggleParticlePanel(): void {
    if (!this.scene.particlePanel) this.createParticlePanel();
    this.scene.particlePanel?.toggle();
  }

  public ensureParticleManager(): ParticleManager {
    if (this.scene.particleManager) return this.scene.particleManager;
    this.scene.particleManager = new ParticleManager(this.scene, {
      getEntity: (entityId: string): EntityRenderable | undefined => {
        const p = this.scene.playerObjects.get(entityId);
        if (p) return p as unknown as EntityRenderable;
        const n = this.scene.npcObjects.get(entityId);
        if (n) return n as unknown as EntityRenderable;
        return undefined;
      },
      getItemGround: (id: string): ItemGroundRenderable | undefined =>
        this.scene.groundItemObjects.get(id),
      now: () => this.scene.time.now,
    });
    return this.scene.particleManager;
  }

  public playParticleFxOnLocalPlayer(fxId: string, def: ParticleEffectDefV2): string {
    if (!this.scene.localSessionId) return 'v1';
    if (!this.scene.textures.exists(PARTICLE_SHEET.key)) {
      console.warn(`[ParticleFX] texture ${PARTICLE_SHEET.key} no cargada todavía`);
      return 'v1';
    }

    // Camera FX local
    this.applyCameraFxFromDef(def);

    const mgr = this.ensureParticleManager();
    const slot = pickPreviewSlotForFx(def);
    const handle = mgr.spawn(fxId, {
      kind: 'entity',
      entityId: this.scene.localSessionId,
      slot,
    });
    if (!handle) {
      console.warn(`[ParticleFX] spawn falló para ${fxId}`);
      return 'v1';
    }
    this.scene.activeNativeHandles.push(handle);
    // El motor real lo decide el manager — para mostrar al user, asumimos
    // 'v2' si la def es compatible con adapter, 'v1' si tiene orbit/etc.
    const motor = (def.orbit || def.depthMode === 'orbit' || def.subEmitters || def.trail || def.noise)
      ? 'v1'
      : 'v2';
    console.log(`[ParticleFX] ${fxId} → motor ${motor} (handle=${handle.id})`);
    return motor;
  }

  public handlePlayEntityFx(message: PlayEntityFxMessage): void {
    const def = getParticleEffect(message.fxId);
    if (!def) {
      console.warn(`[ParticleFX] PLAY_ENTITY_FX: fx desconocido ${message.fxId}`);
      return;
    }
    if (!this.scene.textures.exists(PARTICLE_SHEET.key)) {
      console.warn(`[ParticleFX] texture ${PARTICLE_SHEET.key} no cargada todavía`);
      return;
    }
    const slot = pickPreviewSlotForFx(def);
    // Anchor a tile fijo si el server mandó coords (FX que debe quedarse en el
    // mundo, ej. portal en el tile de origen de un teleport); si no, a la entidad.
    const handle =
      message.x !== undefined && message.y !== undefined
        ? this.ensureParticleManager().spawn(message.fxId, {
            kind: 'tile',
            tx: message.x,
            ty: message.y,
          })
        : this.ensureParticleManager().spawn(message.fxId, {
            kind: 'entity',
            entityId: message.entityId,
            slot,
          });
    if (!handle) return;
    this.scene.activeNativeHandles.push(handle);
  }

  public updateActiveParticleEmitters(delta: number): void {
    this.scene.particleManager?.updateAll(delta);
  }

  public registerMapParticles(mapData: MapData): void {
    for (const h of this.scene.mapParticleHandles) this.scene.particleManager?.stop(h);
    this.scene.mapParticleHandles = [];
    if (!mapData.particles || mapData.particles.length === 0) return;
    const mgr = this.ensureParticleManager();
    for (const p of mapData.particles) {
      const worldX = p.x * TILE_SIZE + TILE_SIZE / 2;
      const worldY = p.y * TILE_SIZE + TILE_SIZE / 2;
      const handle = mgr.spawn(p.fxId, { kind: 'world', x: worldX, y: worldY });
      if (handle) this.scene.mapParticleHandles.push(handle);
    }
  }

  public playMeleeSwingFx(entityId: string, direction: Direction): void {
    const render = this.scene.playerObjects.get(entityId) ?? this.scene.npcObjects.get(entityId);
    if (!render) return;
    const headingRad =
      direction === Direction.RIGHT
        ? 0
        : direction === Direction.DOWN
          ? Math.PI / 2
          : direction === Direction.LEFT
            ? Math.PI
            : -Math.PI / 2;

    const slash = this.scene.add.graphics().setDepth(650);
    slash.lineStyle(3, 0xffffff, 0.85);
    slash.beginPath();
    slash.arc(0, 0, TILE_SIZE * 0.55, Phaser.Math.DegToRad(-48), Phaser.Math.DegToRad(48));
    slash.strokePath();
    slash.setPosition(render.sprite.x, render.sprite.y - TILE_SIZE * 0.5);
    slash.setRotation(headingRad);
    slash.setScale(0.6);

    this.scene.tweens.add({
      targets: slash,
      scale: 1.15,
      alpha: 0,
      duration: 170,
      ease: 'Quad.easeOut',
      onComplete: () => slash.destroy(),
    });
  }

}
